/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { AbsoluteCellRange } from '../../AbsoluteCellRange';
import { CellError, ErrorType } from '../../Cell';
import { ErrorMessage } from '../../error-message';
import { AstNodeType } from '../../parser';
import { coerceScalarToBoolean, coerceScalarToString, coerceToRange } from '../ArithmeticHelper';
import { getRawValue, isExtendedNumber } from '../InterpreterValue';
import { SimpleRangeValue } from '../SimpleRangeValue';
export var ArgumentTypes;
(function (ArgumentTypes) {
    /**
     * String type.
     */
    ArgumentTypes["STRING"] = "STRING";
    /**
     * Floating point type.
     */
    ArgumentTypes["NUMBER"] = "NUMBER";
    /**
     * Boolean type.
     */
    ArgumentTypes["BOOLEAN"] = "BOOLEAN";
    /**
     * Any non-range value.
     */
    ArgumentTypes["SCALAR"] = "SCALAR";
    /**
     * Any non-range, no-error type.
     */
    ArgumentTypes["NOERROR"] = "NOERROR";
    /**
     * Range type.
     */
    ArgumentTypes["RANGE"] = "RANGE";
    /**
     * Integer type.
     */
    ArgumentTypes["INTEGER"] = "INTEGER";
    /**
     * String representing complex number.
     */
    ArgumentTypes["COMPLEX"] = "COMPLEX";
    /**
     * Range or scalar.
     */
    ArgumentTypes["ANY"] = "ANY";
})(ArgumentTypes || (ArgumentTypes = {}));
/**
 * Abstract class representing interpreter function plugin.
 * Plugin may contain multiple functions. Each function should be of type {@link PluginFunctionType} and needs to be
 * included in {@link implementedFunctions}
 */
export class FunctionPlugin {
    constructor(interpreter) {
        this.coerceScalarToNumberOrError = (arg) => this.interpreter.arithmeticHelper.coerceScalarToNumberOrError(arg);
        this.runFunction = (args, formulaAddress, functionDefinition, fn) => {
            return this.runFunctionTemplate(args, formulaAddress, functionDefinition, fn);
        };
        this.runMatrixFunction = (args, formulaAddress, functionDefinition, fn) => {
            return this.runFunctionTemplate(args, formulaAddress, functionDefinition, fn);
        };
        this.runFunctionTemplate = (args, formulaAddress, functionDefinition, fn) => {
            var _a, _b, _c;
            const argumentDefinitions = functionDefinition.parameters;
            let scalarValues;
            if (functionDefinition.expandRanges) {
                scalarValues = this.listOfScalarValues(args, formulaAddress);
            }
            else {
                scalarValues = args.map((ast) => [this.evaluateAst(ast, formulaAddress), false]);
            }
            const coercedArguments = [];
            let argCoerceFailure = undefined;
            if (functionDefinition.repeatLastArgs === undefined && argumentDefinitions.length < scalarValues.length) {
                return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
            }
            if (functionDefinition.repeatLastArgs !== undefined && scalarValues.length > argumentDefinitions.length &&
                (scalarValues.length - argumentDefinitions.length) % functionDefinition.repeatLastArgs !== 0) {
                return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
            }
            for (let i = 0, j = 0; i < Math.max(scalarValues.length, argumentDefinitions.length); i++, j++) {
                // i points to where are we in the scalarValues list,
                // j points to where are we in the argumentDefinitions list
                if (j === argumentDefinitions.length) {
                    j -= functionDefinition.repeatLastArgs;
                }
                const [val, ignorable] = (_a = scalarValues[i]) !== null && _a !== void 0 ? _a : [undefined, undefined];
                const arg = val !== null && val !== void 0 ? val : (_b = argumentDefinitions[j]) === null || _b === void 0 ? void 0 : _b.defaultValue;
                if (arg === undefined) {
                    if ((_c = argumentDefinitions[j]) === null || _c === void 0 ? void 0 : _c.optionalArg) {
                        coercedArguments.push(undefined);
                    }
                    else {
                        //not enough values passed as arguments, and there was no default value and argument was not optional
                        return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
                    }
                }
                else {
                    //we apply coerce only to non-default values
                    const coercedArg = val !== undefined ? this.coerceToType(arg, argumentDefinitions[j]) : arg;
                    if (coercedArg !== undefined) {
                        if (coercedArg instanceof CellError && argumentDefinitions[j].argumentType !== ArgumentTypes.SCALAR) {
                            //if this is first error encountered, store it
                            argCoerceFailure = argCoerceFailure !== null && argCoerceFailure !== void 0 ? argCoerceFailure : coercedArg;
                        }
                        coercedArguments.push(coercedArg);
                    }
                    else if (!ignorable) {
                        //if this is first error encountered, store it
                        argCoerceFailure = argCoerceFailure !== null && argCoerceFailure !== void 0 ? argCoerceFailure : (new CellError(ErrorType.VALUE, ErrorMessage.WrongType));
                    }
                }
            }
            return argCoerceFailure !== null && argCoerceFailure !== void 0 ? argCoerceFailure : this.returnNumberWrapper(fn(...coercedArguments), functionDefinition.returnNumberType);
        };
        this.runFunctionWithReferenceArgument = (args, formulaAddress, argumentDefinitions, noArgCallback, referenceCallback, nonReferenceCallback = () => new CellError(ErrorType.NA, ErrorMessage.CellRefExpected)) => {
            if (args.length === 0) {
                return this.returnNumberWrapper(noArgCallback(), argumentDefinitions.returnNumberType);
            }
            else if (args.length > 1) {
                return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
            }
            let arg = args[0];
            while (arg.type === AstNodeType.PARENTHESIS) {
                arg = arg.expression;
            }
            let cellReference;
            if (arg.type === AstNodeType.CELL_REFERENCE) {
                cellReference = arg.reference.toSimpleCellAddress(formulaAddress);
            }
            else if (arg.type === AstNodeType.CELL_RANGE || arg.type === AstNodeType.COLUMN_RANGE || arg.type === AstNodeType.ROW_RANGE) {
                try {
                    cellReference = AbsoluteCellRange.fromAst(arg, formulaAddress).start;
                }
                catch (e) {
                    return new CellError(ErrorType.REF, ErrorMessage.CellRefExpected);
                }
            }
            if (cellReference !== undefined) {
                return this.returnNumberWrapper(referenceCallback(cellReference), argumentDefinitions.returnNumberType);
            }
            return this.runFunction(args, formulaAddress, argumentDefinitions, nonReferenceCallback);
        };
        this.interpreter = interpreter;
        this.dependencyGraph = interpreter.dependencyGraph;
        this.columnSearch = interpreter.columnSearch;
        this.config = interpreter.config;
        this.serialization = interpreter.serialization;
    }
    evaluateAst(ast, formulaAddress) {
        return this.interpreter.evaluateAst(ast, formulaAddress);
    }
    listOfScalarValues(asts, formulaAddress) {
        const ret = [];
        for (const argAst of asts) {
            const value = this.evaluateAst(argAst, formulaAddress);
            if (value instanceof SimpleRangeValue) {
                for (const scalarValue of value.valuesFromTopLeftCorner()) {
                    ret.push([scalarValue, true]);
                }
            }
            else {
                ret.push([value, false]);
            }
        }
        return ret;
    }
    coerceToType(arg, coercedType) {
        let ret;
        if (arg instanceof SimpleRangeValue) {
            switch (coercedType.argumentType) {
                case ArgumentTypes.RANGE:
                case ArgumentTypes.ANY:
                    ret = arg;
                    break;
                default:
                    return undefined;
            }
        }
        else {
            switch (coercedType.argumentType) {
                case ArgumentTypes.INTEGER:
                case ArgumentTypes.NUMBER:
                    // eslint-disable-next-line no-case-declarations
                    const coerced = this.coerceScalarToNumberOrError(arg);
                    if (!isExtendedNumber(coerced)) {
                        ret = coerced;
                        break;
                    }
                    // eslint-disable-next-line no-case-declarations
                    const value = getRawValue(coerced);
                    if (coercedType.maxValue !== undefined && value > coercedType.maxValue) {
                        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
                    }
                    if (coercedType.minValue !== undefined && value < coercedType.minValue) {
                        return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
                    }
                    if (coercedType.lessThan !== undefined && value >= coercedType.lessThan) {
                        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
                    }
                    if (coercedType.greaterThan !== undefined && value <= coercedType.greaterThan) {
                        return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
                    }
                    if (coercedType.argumentType === ArgumentTypes.INTEGER && !Number.isInteger(value)) {
                        return new CellError(ErrorType.NUM, ErrorMessage.IntegerExpected);
                    }
                    ret = coerced;
                    break;
                case ArgumentTypes.STRING:
                    ret = coerceScalarToString(arg);
                    break;
                case ArgumentTypes.BOOLEAN:
                    ret = coerceScalarToBoolean(arg);
                    break;
                case ArgumentTypes.SCALAR:
                case ArgumentTypes.NOERROR:
                case ArgumentTypes.ANY:
                    ret = arg;
                    break;
                case ArgumentTypes.RANGE:
                    if (arg instanceof CellError) {
                        return arg;
                    }
                    ret = coerceToRange(getRawValue(arg));
                    break;
                case ArgumentTypes.COMPLEX:
                    return this.interpreter.arithmeticHelper.coerceScalarToComplex(getRawValue(arg));
            }
        }
        if (coercedType.passSubtype || ret === undefined) {
            return ret;
        }
        else {
            return getRawValue(ret);
        }
    }
    metadata(name) {
        const params = this.constructor.implementedFunctions[name];
        if (params !== undefined) {
            return params;
        }
        throw new Error(`No metadata for function ${name}.`);
    }
    returnNumberWrapper(val, type, format) {
        if (type !== undefined && isExtendedNumber(val)) {
            return this.interpreter.arithmeticHelper.ExtendedNumberFactory(getRawValue(val), { type, format });
        }
        else {
            return val;
        }
    }
}
